import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslocoDirective } from '@jsverse/transloco';
import { SignupStore } from '../../domain/signup.store';
import { LEGAL_DOCUMENT_VERSIONS } from '../legal-documents';

/**
 * Step five: what is being agreed to.
 *
 * **Three separate controls, and the third must not be bundled with the first two.** The Terms of
 * Service and the DPA are required — a submitted form is their acceptance, which is why the payload
 * carries versions rather than booleans. Marketing is consent under § 7 UWG, which applies to B2B
 * advertising email too, and consent obtained by bundling it with something the customer had to
 * tick anyway is not consent. So it is its own checkbox, **not pre-ticked**, and a customer can
 * complete the funnel without it.
 *
 * **What is submitted is the document *versions*, not the ticks.** "They agreed" only means
 * something as "they agreed to this text", and the version is the evidence a boolean would merely
 * duplicate. The server stamps who, when and from where — a client-supplied acceptance timestamp or
 * IP address would be evidence of nothing.
 *
 * The versions come from {@link LEGAL_DOCUMENT_VERSIONS} rather than from constants here, so that
 * revising the legal texts does not mean rebuilding this library. Whichever version this step
 * displays is the version it records, which is the whole point of it knowing.
 */
@Component({
  selector: 'pp-consent-step',
  imports: [ReactiveFormsModule, MatCheckbox, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup.consent'">
      <p class="step-lead">{{ t('lead') }}</p>

      <form [formGroup]="form" class="consent-step-form">
        <mat-checkbox id="signup-consent-tos" formControlName="acceptedTermsOfService" test-id="signup-consent-tos">
          <span
            >{{ t('termsOfService') }}
            <a [href]="documents.termsOfServiceUrl" target="_blank" rel="noopener noreferrer">{{ t('read') }}</a>
            <span class="consent-version">({{ t('version', { version: documents.termsOfServiceVersion }) }})</span>
          </span>
        </mat-checkbox>
        @if (form.controls.acceptedTermsOfService.touched && !form.controls.acceptedTermsOfService.value) {
          <p class="consent-error">{{ t('termsOfServiceRequired') }}</p>
        }

        <mat-checkbox id="signup-consent-dpa" formControlName="acceptedDataProcessingAgreement" test-id="signup-consent-dpa">
          <span
            >{{ t('dataProcessingAgreement') }}
            <a [href]="documents.dataProcessingAgreementUrl" target="_blank" rel="noopener noreferrer">{{ t('read') }}</a>
            <span class="consent-version">({{ t('version', { version: documents.dataProcessingAgreementVersion }) }})</span>
          </span>
        </mat-checkbox>
        @if (form.controls.acceptedDataProcessingAgreement.touched && !form.controls.acceptedDataProcessingAgreement.value) {
          <p class="consent-error">{{ t('dataProcessingAgreementRequired') }}</p>
        }

        <!-- Separate, optional, and deliberately not pre-ticked. See the class comment. -->
        <mat-checkbox id="signup-consent-marketing" formControlName="marketingOptIn" test-id="signup-consent-marketing">
          {{ t('marketing') }}
        </mat-checkbox>
        <p class="consent-note">{{ t('marketingNote') }}</p>

        <p class="consent-note">{{ t('recordNote') }}</p>
      </form>
    </ng-container>
  `,
  styles: [
    `
      .consent-step-form {
        display: flex;
        flex-direction: column;
        row-gap: 12px;
        max-width: 560px;
      }

      .consent-version {
        opacity: 0.6;
        font-size: 0.85rem;
      }

      .consent-note {
        font-size: 0.85rem;
        opacity: 0.75;
        margin: 0;
      }

      .consent-error {
        color: var(--mat-sys-error, #b3261e);
        font-size: 0.85rem;
        margin: 0;
      }
    `,
  ],
})
export class ConsentStepComponent implements OnInit {
  readonly documents = inject(LEGAL_DOCUMENT_VERSIONS);
  private readonly store = inject(SignupStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * `requiredTrue` on the two required boxes, rather than `required`: `required` is satisfied by
   * `false` on a checkbox, which would let the funnel submit with the boxes clear and record an
   * acceptance nobody gave.
   */
  readonly form = this.formBuilder.nonNullable.group({
    acceptedTermsOfService: [false, Validators.requiredTrue],
    acceptedDataProcessingAgreement: [false, Validators.requiredTrue],
    marketingOptIn: [false],
  });

  ngOnInit(): void {
    // Restored from a draft as ticked-or-not, derived from whether a version was recorded. The
    // draft carries versions rather than booleans, so this is the one place the two representations
    // meet — and it has to be a read of *this* version, not merely of "something was accepted": a
    // customer returning after the documents were revised has to agree to the new text.
    const consent = this.store.draft().consent;
    this.form.patchValue({
      acceptedTermsOfService: consent?.termsOfServiceVersion === this.documents.termsOfServiceVersion,
      acceptedDataProcessingAgreement: consent?.dataProcessingAgreementVersion === this.documents.dataProcessingAgreementVersion,
      marketingOptIn: consent?.marketingOptIn ?? false,
    });
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.push());
  }

  /**
   * Writes the *versions* that were displayed, and only for the boxes that are ticked.
   *
   * An unticked box records no version rather than an empty string: the store's completeness check
   * reads the versions, so a blank one is what stops the submit — the same fact the form reports as
   * an unticked required box.
   */
  private push(): void {
    const value = this.form.getRawValue();
    this.store.updateDraft({
      consent: {
        termsOfServiceVersion: value.acceptedTermsOfService ? this.documents.termsOfServiceVersion : undefined,
        dataProcessingAgreementVersion: value.acceptedDataProcessingAgreement ? this.documents.dataProcessingAgreementVersion : undefined,
        marketingOptIn: value.marketingOptIn,
      },
    });
  }
}
